import React, { memo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Collapse,
  Fade,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Compass } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import TourCarousel from '../tour';

const FilesEmpty: React.FC = () => {
  const [showTour, setShowTour] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box>
      {/* Minimal Empty State */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: showTour ? 0 : 'calc(100vh - 300px)',
          position: 'relative',
          transition: 'height 0.3s ease',
          overflow: 'hidden',
          pt: showTour ? 0 : 5,
        }}
      >
        <Fade in={!showTour} timeout={300}>
          <ArrowUpwardIcon
            sx={{
              position: 'absolute',
              top: 5,
              fontSize: 40,
              color: 'text.secondary',
              animation: 'bounce 2s infinite',
            }}
          />
        </Fade>

        <Fade in={!showTour} timeout={300}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{ mt: 8, mb: 2, fontWeight: 500 }}
              color="text.secondary"
            >
              {t('library.empty.title')}
            </Typography>

            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{ maxWidth: 600, mb: 4 }}
            >
              {t('library.empty.description')}
            </Typography>
          </Box>
        </Fade>

        {/* Tour Toggle Button - Only show when tour is hidden */}
        {!showTour && (
          <Button
            variant="contained"
            onClick={() => setShowTour(true)}
            startIcon={
              <Compass style={{ color: theme.palette.text.secondary }} />
            }
            sx={{
              textTransform: 'none',
              borderRadius: 6,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.background.paper,
              color: 'text.secondary',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                boxShadow: 'none',
              },
            }}
          >
            {t('library.empty.explore')}
          </Button>
        )}
      </Box>

      {/* Collapsible Tour Section */}
      <Collapse in={showTour} timeout={400}>
        <Box sx={{ mt: -4 }}>
          <TourCarousel />
        </Box>
      </Collapse>

      <style>
        {`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(FilesEmpty);
